import { HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';
import { ApiQuery } from '../../core/api/entities/api-query.entity';
import { ParsePaginatedMetadataResponse } from '../../core/api/helpers/parse-paginated-metadata-response.helper';
import { Paginated } from '../../core/common/entities/mediahaven-api/paginated.entity';
import { RecordSearchRequest } from '../../core/http/entities/search-params.entity';
import { ShAbstractHttpService } from '../../core/http/services/http.abstract.service';
import { EmptyStructuredQuery } from '../../core/query/entities/empty-structured-query.constant';
import { LogicalOperand, Operand } from '../../core/query/entities/operand.entity';
import { StructuredQuery } from '../../core/query/entities/structured-query.entity';
import { ApiFragment } from '../../core/records/entities/api-fragment.class';
import { Metadata, RecordId } from '../../core/records/entities/metadata/metadata.entity';
import { titleAscSortInfo } from '../../overviews/generic-overview/components/display-settings-bar/entities/sort-info.entity';
import { PaginatedResults } from '../../overviews/generic-overview/components/pagination/entities/paginated-results.entity';
import { PaginationInfo } from '../../overviews/generic-overview/components/pagination/entities/pagination-info.entity';
import { Face } from '../entities/face.entity';
import { isSetPerson } from '../entities/person.entity';

enum FaceAction {
    AssignPerson = 'AssignPerson',
    UnassignPerson = 'UnassignPerson',
    RemoveFace = 'RemoveFace'
}

@Injectable({
    providedIn: 'root'
})
export class PersonDataService {
    private readonly http: ShAbstractHttpService = inject(ShAbstractHttpService);

    private readonly ENDPOINT = 'v2/persons';
    private readonly LINK_ENDPOINT = 'v2/records';
    private readonly DEFAULT_HEADERS: HttpHeaders = new HttpHeaders().set('accept', 'application/hal+json');

    getPersons(searchTerm: string, paginationInfo: PaginationInfo): Observable<PaginatedResults<ApiFragment>> {
        const structuredQuery: StructuredQuery = {
            ...EmptyStructuredQuery,
            AdvancedSearch: {
                LogicalOperand: LogicalOperand.And,
                Groups: [
                    {
                        LogicalOperand: LogicalOperand.And,
                        Terms: [
                            {
                                DottedKey: 'Descriptive.Title',
                                Operand: Operand.Include,
                                Values: [`*${searchTerm}*`]
                            }
                        ]
                    }
                ]
            }
        };

        const query: ApiQuery = new ApiQuery(structuredQuery);

        const params = new RecordSearchRequest(query, paginationInfo, titleAscSortInfo).toHttpParams();

        return this.http
            .get<Paginated<Metadata>>(this.ENDPOINT, params, this.DEFAULT_HEADERS)
            .pipe(
                map(paginatedResult => ParsePaginatedMetadataResponse(paginatedResult, paginationInfo.resultsPerPage))
            );
    }

    getPerson(recordId: RecordId): Observable<ApiFragment | null> {
        const endpoint = `${this.ENDPOINT}/${recordId}`;

        return this.http.get<Metadata>(endpoint, undefined, this.DEFAULT_HEADERS).pipe(
            map(personMetadata => new ApiFragment(personMetadata)),
            catchError(error => of(null))
        );
    }

    createPerson(faceId: string, name: string): Observable<ApiFragment> {
        const newPerson = {
            Name: name,
            FaceId: faceId,
            Force: true // Always force to avoid conflicts as much as possible
        };
        return this.http
            .post<Metadata>(this.ENDPOINT, newPerson)
            .pipe(map(personRecord => new ApiFragment(personRecord)));
    }

    updatePerson(faceId: string, personId: string, name: string): Observable<ApiFragment> {
        const updatedPerson = {
            Name: name,
            ExcludeFaceId: faceId
        };
        return this.http
            .put<Metadata>(`${this.ENDPOINT}/${personId}`, updatedPerson)
            .pipe(map(personRecord => new ApiFragment(personRecord)));
    }

    deleteFace(recordId: string, faceId: string): Observable<boolean> {
        return this.http.delete<void>(`${this.LINK_ENDPOINT}/${recordId}/faces/${faceId}`).pipe(map(() => true));
    }

    updateFaces(recordId: string, updatedFaces: Face[], deletedFaces: string[]): Observable<ApiFragment> {
        const updated = updatedFaces.map(face => ({
            Id: face.Id,
            Person: {
                RecordId: face.Person?.RecordId ?? undefined,
                ...(isSetPerson(face.Person) && { Name: face.Person?.Name })
            },
            Action: face.Person?.RecordId ? FaceAction.AssignPerson : FaceAction.UnassignPerson
        }));

        const deleted = deletedFaces.map(faceId => ({
            Id: faceId,
            Action: FaceAction.RemoveFace
        }));

        const facesUpdate = {
            Faces: {
                Face: [...updated, ...deleted]
            }
        };

        return this.http
            .post<Metadata>(`${this.LINK_ENDPOINT}/${recordId}/faces`, facesUpdate)
            .pipe(map(personRecord => new ApiFragment(personRecord)));
    }
}
